import { useEffect, useMemo, useState } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Link, useNavigate, useLocation } from "react-router-dom";
import axios from "axios";
import { Toaster, toast } from "sonner";
import { Search, RefreshCw, ExternalLink, Bird, MapPin, Leaf, Flame, ArrowLeft, Calendar, Sparkles, Shuffle, Map as MapIcon } from "lucide-react";
import MonthlyBirdMapPage from "./MonthlyBirdMapPage";
import ShareMenu from "./ShareMenu";
import { useBackToHome } from "./lib/use-back";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Badge } from "@/components/ui/badge";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const HERO_TEXTURE =
  "https://static.prod-images.emergentagent.com/jobs/aaab1416-d5d2-4732-bd3c-37b3a2d98a89/images/4fbd6a6dae5a3ecce46f82f950af29b3c1c20ff069e3a37e282d6b92061d0d47.png";
const FIELD_GUIDE_IMG =
  "https://static.prod-images.emergentagent.com/jobs/aaab1416-d5d2-4732-bd3c-37b3a2d98a89/images/f41e323ed1365332aa820325e61e3dd37c9b12f356eb2f9e220850ccb69e4b4c.png";

const formatRelative = (iso) => {
  if (!iso) return "—";
  const then = new Date(iso).getTime();
  const diff = Date.now() - then;
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins} min ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs} h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 30) return `${days} d ago`;
  return new Date(iso).toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const Header = ({ username, lastSync }) => (
  <header
    data-testid="app-header"
    className="sticky top-0 z-30 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-[#E5E2DA]"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
      <div className="flex items-center gap-3">
        <div className="w-9 h-9 rounded-full bg-[#2C5530] flex items-center justify-center">
          <Bird size={18} strokeWidth={1.6} className="text-[#FAF9F6]" />
        </div>
        <div className="leading-tight">
          <div className="font-serif-display text-xl tracking-tight text-[#1A1A1A]">
            In the Green Lane
          </div>
        </div>
      </div>

      <div className="flex items-center gap-3">
        <div
          data-testid="last-sync-indicator"
          className="hidden md:flex items-center gap-2 font-mono text-xs text-[#5C5C5C]"
          title="Auto-sync runs in the background"
        >
          <span className="w-1.5 h-1.5 rounded-full bg-[#4A7C59] animate-pulse" />
          auto-sync · {lastSync ? formatRelative(lastSync) : "—"}
        </div>
        <ShareMenu />
      </div>
    </div>
  </header>
);

const Hero = ({ stats, onBirdOfTheDay }) => (
  <section
    data-testid="hero-section"
    className="relative overflow-hidden border-b border-[#E5E2DA]"
  >
    <div
      className="absolute inset-0 opacity-50"
      style={{
        backgroundImage: `url(${HERO_TEXTURE})`,
        backgroundSize: "cover",
        backgroundPosition: "center",
      }}
    />
    <div className="absolute inset-0 bg-gradient-to-b from-[#FAF9F6]/40 via-[#FAF9F6]/70 to-[#FAF9F6]" />
    <div className="grain" />

    <div className="relative max-w-7xl mx-auto px-5 sm:px-6 lg:px-10 pt-14 sm:pt-20 pb-10 sm:pb-14 rise">
      <div className="max-w-3xl">
        <div className="label-eyebrow mb-5 flex items-center gap-2">
          <Leaf size={12} strokeWidth={1.8} className="text-[#2C5530]" />
          A living catalogue
        </div>
        <h1
          data-testid="hero-title"
          className="font-serif-display text-[2.15rem] sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[1.02] whitespace-nowrap"
        >
          Every bird, <em className="italic text-[#2C5530]">indexed</em>.
        </h1>
        <p
          data-testid="hero-description"
          className="mt-5 sm:mt-6 text-[11px] min-[375px]:text-xs sm:text-lg text-[#5C5C5C] max-w-xl leading-relaxed"
        >
          A field-guide companion to{" "}
          <a
            href="https://instagram.com/inthegreenlane"
            target="_blank"
            rel="noreferrer"
            data-testid="hero-instagram-link"
            className="text-[#2C5530] underline decoration-[#C17767] underline-offset-4 hover:text-[#1E3A21]"
          >
            @inthegreenlane
          </a>
          . Follow me on my birding journey, and if you found the tools below
          helpful, don&apos;t keep this page a secret. Share it so that we can
          all get the lifers we seek. I&apos;ll see you in the field!
        </p>
        <div className="mt-7 flex flex-col sm:flex-row sm:flex-wrap gap-3">
          <button
            type="button"
            data-testid="bird-of-the-day-button"
            onClick={onBirdOfTheDay}
            className="group inline-flex items-center gap-2 rounded-full bg-[#C17767] text-[#FAF9F6] hover:bg-[#A55F4F] transition-colors px-5 py-2.5 font-mono text-xs tracking-wider uppercase font-medium"
          >
            <Sparkles size={14} strokeWidth={1.8} />
            Bird of the day
            <span className="ml-1 transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </button>
          <Link
            to="/rare-sightings"
            data-testid="rare-sightings-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#2C5530] text-[#FAF9F6] hover:bg-[#1E3A21] transition-colors px-5 py-2.5 font-mono text-xs tracking-wider uppercase font-medium"
          >
            <Flame size={14} strokeWidth={1.8} className="text-[#FAF9F6]" />
            Rare sightings
            <span className="ml-1 transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </Link>
          <Link
            to="/monthly-bird-map"
            data-testid="monthly-bird-map-button"
            className="group inline-flex items-center gap-2 rounded-full bg-[#1A1A1A] text-[#FAF9F6] hover:bg-[#2C5530] transition-colors px-5 py-2.5 font-mono text-xs tracking-wider uppercase font-medium"
          >
            <MapIcon size={14} strokeWidth={1.8} />
            Monthly bird map
            <span className="ml-1 transition-transform group-hover:translate-x-0.5">
              →
            </span>
          </Link>
        </div>
      </div>

      <div
        data-testid="species-count-display"
        className="mt-10 sm:mt-12 pt-6 border-t border-[#E5E2DA] flex items-baseline gap-4"
      >
        <span className="label-eyebrow">Unique species spotted</span>
        <span
          data-testid="species-count-value"
          className="font-serif-display text-5xl sm:text-6xl font-light tracking-tighter text-[#2C5530] leading-none"
        >
          {stats ? String(stats.total_species).padStart(2, "0") : "—"}
        </span>
      </div>
    </div>
  </section>
);

const SearchAndSort = ({ query, setQuery, sort, setSort, count }) => (
  <div
    data-testid="search-toolbar"
    className="sticky top-16 z-20 bg-[#FAF9F6]/85 backdrop-blur-xl border-b border-[#E5E2DA]"
  >
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-4 flex flex-col md:flex-row md:items-center gap-3">
      <div className="relative flex-1">
        <Search
          size={16}
          strokeWidth={1.8}
          className="absolute left-4 top-1/2 -translate-y-1/2 text-[#5C5C5C]"
        />
        <Input
          data-testid="search-bird-input"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          placeholder="Search a species — e.g. Great Egret, Cardinalis cardinalis…"
          className="pl-11 pr-4 h-12 rounded-full border-[#E5E2DA] bg-white focus-visible:ring-[#2C5530] focus-visible:ring-offset-0 text-base"
        />
      </div>
      <div className="flex items-center gap-3">
        <span className="label-eyebrow whitespace-nowrap">
          {count} result{count === 1 ? "" : "s"}
        </span>
        <Select value={sort} onValueChange={setSort}>
          <SelectTrigger
            data-testid="sort-select"
            className="w-[180px] h-12 rounded-full border-[#E5E2DA] bg-white"
          >
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="name" data-testid="sort-name">A → Z</SelectItem>
            <SelectItem value="recent" data-testid="sort-recent">Recently seen</SelectItem>
            <SelectItem value="count" data-testid="sort-count">Most sighted</SelectItem>
          </SelectContent>
        </Select>
      </div>
    </div>
  </div>
);

const SpeciesCard = ({ species, onOpen }) => (
  <button
    data-testid={`bird-card-${species.id}`}
    onClick={() => onOpen(species)}
    className="field-card group text-left rise"
  >
    <div className="relative aspect-[4/5] bg-[#EFEBE0] overflow-hidden">
      {species.cover_media_url ? (
        <img
          src={species.cover_media_url}
          alt={species.common_name}
          className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
          loading="lazy"
        />
      ) : (
        <div className="w-full h-full flex items-center justify-center text-[#8C7A6B]">
          <Bird size={48} strokeWidth={1.2} />
        </div>
      )}
      <div className="absolute top-3 left-3">
        <Badge className="bg-[#FAF9F6]/90 text-[#2C5530] hover:bg-white border border-[#E5E2DA] font-mono text-[10px] tracking-wider px-2.5 py-0.5">
          ×{species.sighting_count}
        </Badge>
      </div>
    </div>
    <div className="p-5">
      <div className="font-serif-display text-xl text-[#1A1A1A] leading-snug">
        {species.common_name}
      </div>
      {species.scientific_name && (
        <div className="font-serif-display italic text-sm text-[#5C5C5C] mt-1">
          {species.scientific_name}
        </div>
      )}
      <div className="mt-3 flex items-center justify-between">
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B]">
          {formatRelative(species.last_seen)}
        </span>
        <span className="font-mono text-[10px] tracking-widest uppercase text-[#2C5530] group-hover:underline">
          open →
        </span>
      </div>
    </div>
  </button>
);

const EmptyState = ({ query }) => (
  <div
    data-testid="empty-state"
    className="text-center py-24 max-w-md mx-auto rise"
  >
    <img
      src={FIELD_GUIDE_IMG}
      alt="Empty field guide"
      className="w-40 h-40 object-cover rounded-full mx-auto mb-8 grayscale opacity-80"
    />
    <h3 className="font-serif-display text-3xl mb-3">
      {query ? "No matches in the catalogue" : "The catalogue is awakening"}
    </h3>
    <p className="text-[#5C5C5C] text-sm leading-relaxed">
      {query
        ? `Nothing matches “${query}”. Try a partial name or scientific binomial.`
        : "We're pulling the latest posts from @inthegreenlane. The first species will appear here in moments — or hit Sync now."}
    </p>
  </div>
);

const TOAST_OPTIONS = {
  style: {
    background: "#FAF9F6",
    border: "1px solid #E5E2DA",
    color: "#1A1A1A",
    fontFamily: "Work Sans, sans-serif",
  },
};
const SKELETON_KEYS = Array.from({ length: 8 }, (_, n) => `skeleton-${n}`);
const TIME_WINDOWS = ["6–10 AM", "10 AM – 2 PM", "2 – 6 PM", "6 – 10 PM"];

const SkeletonGrid = () => (
  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
    {SKELETON_KEYS.map((k) => (
      <div key={k} className="field-card">
        <div className="aspect-[4/5] shimmer" />
        <div className="p-5 space-y-3">
          <div className="h-5 w-3/4 shimmer rounded" />
          <div className="h-3 w-1/2 shimmer rounded" />
        </div>
      </div>
    ))}
  </div>
);

const SpeciesDialog = ({ species, onClose }) => (
  <Dialog open={!!species} onOpenChange={(o) => !o && onClose()}>
    <DialogContent
      data-testid="species-dialog"
      className="max-w-4xl max-h-[92vh] md:h-[92vh] w-[calc(100vw-1rem)] sm:w-full bg-[#FAF9F6] border-[#E5E2DA] p-0 gap-0 overflow-hidden flex flex-col md:grid md:grid-cols-5"
    >
      {species && (
        <>
          <div className="md:col-span-2 bg-[#EFEBE0] aspect-[4/3] md:aspect-auto md:h-full flex-shrink-0 flex items-center justify-center overflow-hidden">
            {species.cover_media_url ? (
              <img
                src={species.cover_media_url}
                alt={species.common_name}
                className="w-full h-full object-cover object-center md:object-contain"
              />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-[#8C7A6B]">
                <Bird size={64} strokeWidth={1.2} />
              </div>
            )}
          </div>
          <div className="md:col-span-3 flex-1 md:h-full min-h-0 overflow-y-auto p-6 sm:p-8 lg:p-10" data-testid="species-dialog-scroll">
            <DialogHeader>
              <div className="label-eyebrow mb-3">Species record</div>
              <DialogTitle
                data-testid="species-dialog-title"
                className="font-serif-display text-4xl lg:text-5xl font-light tracking-tight text-[#1A1A1A]"
              >
                {species.common_name}
              </DialogTitle>
              {species.scientific_name && (
                <DialogDescription className="font-serif-display italic text-lg text-[#5C5C5C] mt-1">
                  {species.scientific_name}
                </DialogDescription>
              )}
            </DialogHeader>

            {species.description && (
              <p className="mt-6 text-[#1A1A1A] leading-relaxed">
                {species.description}
              </p>
            )}

            <div className="divider-leaf my-7" />

            <div className="grid grid-cols-3 gap-4 font-mono text-xs">
              <div>
                <div className="label-eyebrow mb-1">Sightings</div>
                <div className="text-[#1A1A1A] text-lg font-medium">
                  {species.sighting_count}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">First seen</div>
                <div className="text-[#1A1A1A] text-sm">
                  {formatRelative(species.first_seen)}
                </div>
              </div>
              <div>
                <div className="label-eyebrow mb-1">Last seen</div>
                <div className="text-[#1A1A1A] text-sm">
                  {formatRelative(species.last_seen)}
                </div>
              </div>
            </div>

            <div className="mt-8">
              <div className="label-eyebrow mb-4 flex items-center gap-2">
                <MapPin size={11} strokeWidth={2} /> Featured in posts
              </div>
              <ul className="space-y-3" data-testid="species-sightings-list">
                {(species.sightings || []).map((s, i) => (
                  <li
                    key={s.post_ig_id}
                    className="border border-[#E5E2DA] rounded-sm p-4 bg-white"
                  >
                    <div className="flex items-start gap-4">
                      {s.media_url && (
                        <img
                          src={s.media_url}
                          alt=""
                          className="w-16 h-16 rounded-sm object-cover flex-shrink-0"
                        />
                      )}
                      <div className="flex-1 min-w-0">
                        <div className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B]">
                          {s.timestamp ? new Date(s.timestamp).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "numeric" }) : "—"}
                        </div>
                        {s.caption_snippet && (
                          <p className="text-sm text-[#1A1A1A] mt-1.5 line-clamp-3">
                            {s.caption_snippet}
                          </p>
                        )}
                        {s.permalink && (
                          <a
                            data-testid={`sighting-link-${i}`}
                            href={s.permalink}
                            target="_blank"
                            rel="noreferrer"
                            className="inline-flex items-center gap-1.5 mt-2 text-xs text-[#2C5530] hover:text-[#1E3A21] font-medium"
                          >
                            View on Instagram <ExternalLink size={12} />
                          </a>
                        )}
                      </div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </>
      )}
    </DialogContent>
  </Dialog>
);

const Footer = ({ username }) => (
  <footer className="mt-20 border-t border-[#E5E2DA] bg-[#F4F1E8]">
    <div className="max-w-7xl mx-auto px-6 lg:px-10 py-12 grid md:grid-cols-3 gap-8">
      <div>
        <div className="font-serif-display text-2xl">In the Green Lane</div>
        <p className="text-sm text-[#5C5C5C] mt-2 max-w-sm">
          A community-driven species index, automatically curated from the
          Instagram feed @{username}.
        </p>
      </div>
      <div>
        <div className="label-eyebrow mb-3">Source</div>
        <a
          href={`https://instagram.com/${username}`}
          target="_blank"
          rel="noreferrer"
          className="text-[#2C5530] hover:text-[#1E3A21] inline-flex items-center gap-1.5"
        >
          @{username} <ExternalLink size={12} />
        </a>
      </div>
      <div>
        <div className="label-eyebrow mb-3">Updated</div>
        <p className="font-mono text-xs text-[#5C5C5C]">
          On demand via the <span className="text-[#2C5530]">Sync now</span>{" "}
          button. Powered by Instagram Graph API and Claude Sonnet 4.5.
        </p>
      </div>
    </div>
  </footer>
);

const RareSightingsPage = () => {
  useBackToHome();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [refreshing, setRefreshing] = useState(false);

  const load = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    else setLoading(true);
    setError(null);
    try {
      const r = await axios.get(`${API}/rare-sightings`, {
        params: { sid: "SN36214", days: 7 },
        timeout: 30000,
      });
      setData(r.data);
    } catch (e) {
      setError(
        e?.response?.data?.detail ||
          "Could not reach eBird right now. Try again in a moment."
      );
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    // Defer through a microtask so ESLint cannot statically link
    // setState back to this effect body.
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) load(false);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  // Group sightings by species and compute a per-species date × time-window heatmap.
  // Time windows: 6-10 (0), 10-14 (1), 14-18 (2), 18-22 (3). Anything outside is skipped.
  const grouped = useMemo(() => {
    const bucketFor = (hour) => {
      if (hour >= 6 && hour < 10) return 0;
      if (hour >= 10 && hour < 14) return 1;
      if (hour >= 14 && hour < 18) return 2;
      if (hour >= 18 && hour < 22) return 3;
      return -1;
    };
    const monthNames = [
      "Jan", "Feb", "Mar", "Apr", "May", "Jun",
      "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
    ];
    const parseSGT = (iso) => {
      if (!iso || typeof iso !== "string") return null;
      const m = iso.match(/^(\d{4})-(\d{2})-(\d{2})T(\d{2}):(\d{2})/);
      if (!m) return null;
      const dateKey = `${m[1]}-${m[2]}-${m[3]}`;
      const label = `${monthNames[parseInt(m[2], 10) - 1]} ${m[3]}, ${m[1]}`;
      return { dateKey, label, hour: parseInt(m[4], 10) };
    };

    const groups = new Map();
    for (const s of data?.sightings || []) {
      const key = s.common_name || "Unknown";
      if (!groups.has(key)) {
        groups.set(key, {
          common_name: s.common_name,
          scientific_name: s.scientific_name,
          species_code: s.species_code,
          items: [],
          heatmap: new Map(), // dateKey -> { label, counts: [n,n,n,n] }
          locations: new Set(),
          total: 0,
          max: 0,
          latestIso: "",
        });
      }
      const g = groups.get(key);
      g.items.push(s);
      g.total += 1;
      if ((s.observed_at || "") > g.latestIso) g.latestIso = s.observed_at || "";
      if (s.location_name) g.locations.add(s.location_name);
      const parsed = parseSGT(s.observed_at);
      if (parsed) {
        const b = bucketFor(parsed.hour);
        if (b >= 0) {
          let row = g.heatmap.get(parsed.dateKey);
          if (!row) {
            row = {
              label: parsed.label,
              counts: [0, 0, 0, 0],
              items: [[], [], [], []],
            };
            g.heatmap.set(parsed.dateKey, row);
          }
          row.counts[b] += 1;
          row.items[b].push(s);
          if (row.counts[b] > g.max) g.max = row.counts[b];
        }
      }
    }
    // Sort dates within each species (newest first) and species by most-recent
    const arr = Array.from(groups.values()).map((g) => ({
      ...g,
      dates: Array.from(g.heatmap.entries())
        .sort((a, b) => (b[0] > a[0] ? 1 : -1))
        .map(([k, v]) => ({
          dateKey: k,
          label: v.label,
          counts: v.counts,
          items: v.items,
        })),
      locations: Array.from(g.locations),
    }));
    arr.sort((a, b) => (b.latestIso || "").localeCompare(a.latestIso || ""));
    return arr;
  }, [data]);

  const uniqueSpeciesCount = grouped.length;

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <header
        data-testid="rare-header"
        className="sticky top-0 z-30 backdrop-blur-xl bg-[#FAF9F6]/80 border-b border-[#E5E2DA]"
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-10 h-16 flex items-center justify-between">
          <button
            data-testid="rare-back-button"
            onClick={() => navigate("/")}
            className="inline-flex items-center gap-2 text-[#2C5530] hover:text-[#1E3A21] font-mono text-xs tracking-widest uppercase"
          >
            <ArrowLeft size={14} /> Back to index
          </button>
          <Button
            data-testid="rare-refresh-button"
            onClick={() => load(true)}
            disabled={loading || refreshing}
            variant="outline"
            className="border-[#2C5530] text-[#2C5530] hover:bg-[#2C5530] hover:text-white rounded-full px-5"
          >
            <RefreshCw
              size={14}
              className={`mr-2 ${refreshing ? "animate-spin" : ""}`}
            />
            {refreshing ? "Refreshing…" : "Refresh"}
          </Button>
        </div>
      </header>

      <section className="relative overflow-hidden border-b border-[#E5E2DA]">
        <div className="grain" />
        <div className="relative max-w-7xl mx-auto px-6 lg:px-10 pt-16 pb-12 rise">
          <div className="label-eyebrow mb-5 flex items-center gap-2">
            <Flame size={12} className="text-[#C17767]" strokeWidth={1.8} />
            From eBird Singapore · last 7 days · confirmed only
          </div>
          <h1 className="font-serif-display text-5xl sm:text-6xl lg:text-7xl font-light tracking-tight text-[#1A1A1A] leading-[1.02]">
            Rare <em className="italic text-[#C17767]">sightings</em>.
          </h1>
          <p className="mt-5 text-base text-[#5C5C5C] max-w-2xl leading-relaxed">
            Reports the community has flagged as rare in Singapore over the
            past seven days. Pulled fresh from{" "}
            <a
              href={data?.source_url || "https://ebird.org/alert/summary?sid=SN36214"}
              target="_blank"
              rel="noreferrer"
              className="text-[#2C5530] underline decoration-[#C17767] underline-offset-4 hover:text-[#1E3A21]"
            >
              eBird&apos;s Singapore rare bird alert
            </a>
            .
          </p>
          <div className="mt-8 grid grid-cols-3 gap-6 font-mono text-xs max-w-xl">
            <div>
              <div className="label-eyebrow mb-1">Confirmed sightings</div>
              <div className="text-[#1A1A1A] text-2xl font-medium" data-testid="rare-count">
                {data ? data.count : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Unique species</div>
              <div className="text-[#1A1A1A] text-2xl font-medium">
                {data ? grouped.length : "—"}
              </div>
            </div>
            <div>
              <div className="label-eyebrow mb-1">Last refreshed</div>
              <div className="text-[#1A1A1A] text-sm">
                {data?.fetched_at ? formatRelative(data.fetched_at) : "—"}
              </div>
            </div>
          </div>
        </div>
      </section>

      <main className="max-w-7xl mx-auto px-6 lg:px-10 py-12">
        {loading ? (
          <div className="space-y-4" data-testid="rare-loading">
            {SKELETON_KEYS.slice(0, 5).map((k) => (
              <div
                key={k}
                className="field-card p-6 flex items-center gap-6"
              >
                <div className="w-16 h-16 rounded-full shimmer" />
                <div className="flex-1 space-y-2">
                  <div className="h-5 w-1/3 shimmer rounded" />
                  <div className="h-3 w-1/2 shimmer rounded" />
                </div>
              </div>
            ))}
          </div>
        ) : error ? (
          <div
            data-testid="rare-error"
            className="border border-[#E5C2BA] bg-[#FBEFEC] text-[#7A2E1F] p-6 rounded-sm font-mono text-sm"
          >
            {error}
          </div>
        ) : (data?.sightings || []).length === 0 ? (
          <div data-testid="rare-empty" className="text-center py-24 max-w-md mx-auto">
            <h3 className="font-serif-display text-3xl mb-3">
              The skies are quiet
            </h3>
            <p className="text-[#5C5C5C] text-sm">
              No confirmed rare sightings in the last 7 days. Check back
              soon — birds drift in on every monsoon.
            </p>
          </div>
        ) : (
          <ol
            data-testid="rare-list"
            className="space-y-6"
          >
            {grouped.map((g, idx) => (
              <li
                key={g.species_code || g.common_name}
                data-testid={`rare-item-${idx}`}
                className="rise"
              >
                <div className="field-card p-6 sm:p-8">
                  <div className="flex flex-col sm:flex-row sm:items-baseline sm:justify-between gap-2">
                    <div>
                      <h3
                        data-testid={`rare-species-${idx}`}
                        className="font-serif-display text-2xl text-[#1A1A1A] leading-tight"
                      >
                        {g.common_name}
                      </h3>
                      {g.scientific_name && (
                        <div className="font-serif-display italic text-sm text-[#5C5C5C] mt-1">
                          {g.scientific_name}
                        </div>
                      )}
                    </div>
                    <div className="font-mono text-xs tracking-widest uppercase text-[#8C7A6B]">
                      {g.total} sighting{g.total === 1 ? "" : "s"} · {g.dates.length}{" "}
                      day{g.dates.length === 1 ? "" : "s"}
                    </div>
                  </div>

                  <div className="mt-6 overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="text-[#8C7A6B] font-mono text-[10px] tracking-widest uppercase">
                          <th className="text-left pb-2 pr-4 font-normal">Date</th>
                          <th className="pb-2 px-1 font-normal text-center w-16 sm:w-20">
                            6–10 AM
                          </th>
                          <th className="pb-2 px-1 font-normal text-center w-16 sm:w-20">
                            10–2 PM
                          </th>
                          <th className="pb-2 px-1 font-normal text-center w-16 sm:w-20">
                            2–6 PM
                          </th>
                          <th className="pb-2 px-1 font-normal text-center w-16 sm:w-20">
                            6–10 PM
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {g.dates.map((row) => (
                          <tr key={row.dateKey} className="border-t border-[#E5E2DA]">
                            <td className="py-2 pr-4 font-mono text-xs text-[#1A1A1A] whitespace-nowrap">
                              {row.label}
                            </td>
                            {TIME_WINDOWS.map((win, ci) => {
                              const c = row.counts[ci];
                              const intensity = g.max > 0 ? c / g.max : 0;
                              const bg =
                                c === 0
                                  ? "#F4EFEA"
                                  : `rgba(193, 119, 103, ${Math.max(
                                      0.18,
                                      intensity
                                    )})`;
                              const isPeak = c > 0 && c === g.max;
                              const fg = intensity > 0.55 ? "#FAF9F6" : "#1A1A1A";
                              const cellItems = row.items ? row.items[ci] : [];
                              const cellNode = (
                                <div
                                  title={
                                    c === 0
                                      ? "No sightings"
                                      : `${c} sighting${c === 1 ? "" : "s"}${
                                          isPeak ? " · peak time" : ""
                                        }`
                                  }
                                  className={`mx-auto rounded-sm h-8 sm:h-9 flex items-center justify-center font-mono text-xs transition-transform ${
                                    isPeak ? "ring-2 ring-[#C17767]" : ""
                                  } ${
                                    c > 0
                                      ? "cursor-pointer hover:scale-[1.06]"
                                      : "cursor-default"
                                  }`}
                                  style={{ backgroundColor: bg, color: fg }}
                                >
                                  {c === 0 ? "" : c}
                                </div>
                              );
                              return (
                                <td
                                  key={win}
                                  className="py-1 px-1"
                                  data-testid={`rare-cell-${idx}-${row.dateKey}-${ci}`}
                                >
                                  {c > 0 ? (
                                    <Popover>
                                      <PopoverTrigger asChild>
                                        <button
                                          type="button"
                                          className="block w-full"
                                          aria-label={`${c} sighting${
                                            c === 1 ? "" : "s"
                                          } in this time window`}
                                          data-testid={`rare-cell-trigger-${idx}-${row.dateKey}-${ci}`}
                                        >
                                          {cellNode}
                                        </button>
                                      </PopoverTrigger>
                                      <PopoverContent
                                        side="top"
                                        align="center"
                                        className="w-80 max-w-[calc(100vw-1.5rem)] p-0 bg-[#FAF9F6] border-[#E5E2DA]"
                                        data-testid={`rare-cell-popover-${idx}-${row.dateKey}-${ci}`}
                                      >
                                        <div className="px-4 py-3 border-b border-[#E5E2DA]">
                                          <div className="label-eyebrow text-[#8C7A6B]">
                                            {row.label}
                                          </div>
                                          <div className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B] mt-0.5">
                                            {win}
                                            {" · "}
                                            {c} sighting{c === 1 ? "" : "s"}
                                          </div>
                                        </div>
                                        <ul className="max-h-64 overflow-y-auto divide-y divide-[#E5E2DA]">
                                          {cellItems.map((it) => {
                                            const t = (it.observed_at || "").match(
                                              /T(\d{2}:\d{2})/
                                            );
                                            return (
                                              <li
                                                key={it.obs_id || `${it.observed_at}-${it.location_name}`}
                                                className="px-4 py-3 text-sm"
                                              >
                                                <div className="font-mono text-xs text-[#1A1A1A]">
                                                  {t ? t[1] : "—"}
                                                  {it.number_observed && it.number_observed > 1 && (
                                                    <span className="ml-2 text-[10px] text-[#8C7A6B]">
                                                      ×{it.number_observed}
                                                    </span>
                                                  )}
                                                </div>
                                                {it.location_name && (
                                                  <div className="mt-1">
                                                    {it.map_url ? (
                                                      <a
                                                        href={it.map_url}
                                                        target="_blank"
                                                        rel="noreferrer"
                                                        className="text-[#1A1A1A] hover:text-[#2C5530] hover:underline break-words leading-snug"
                                                      >
                                                        {it.location_name}
                                                      </a>
                                                    ) : (
                                                      <span className="text-[#1A1A1A] leading-snug break-words">
                                                        {it.location_name}
                                                      </span>
                                                    )}
                                                  </div>
                                                )}
                                              </li>
                                            );
                                          })}
                                        </ul>
                                      </PopoverContent>
                                    </Popover>
                                  ) : (
                                    cellNode
                                  )}
                                </td>
                              );
                            })}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {g.locations.length > 0 && (
                    <div className="mt-5 flex items-start gap-2 text-xs text-[#5C5C5C]">
                      <MapPin
                        size={12}
                        strokeWidth={1.8}
                        className="text-[#8C7A6B] mt-0.5 flex-shrink-0"
                      />
                      <div>
                        <div className="font-mono text-[10px] tracking-widest uppercase text-[#8C7A6B] mb-1">
                          Locations
                        </div>
                        <div className="text-[#1A1A1A]">
                          {g.locations.join(" · ")}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </li>
            ))}
          </ol>
        )}
      </main>

      <Toaster position="bottom-right" />
    </div>
  );
};

const BirdOfTheDayDialog = ({ open, post, loading, onSpinAgain, onClose }) => {
  const species = post?.extracted_species || [];
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent
        data-testid="bird-of-the-day-dialog"
        className="max-w-3xl max-h-[92vh] w-[calc(100vw-1rem)] sm:w-full p-0 overflow-hidden bg-[#FAF9F6] border-[#E5E2DA] flex flex-col md:grid md:grid-cols-5"
      >
        <div className="bg-[#EFEBE0] md:col-span-3 aspect-square w-full flex-shrink-0 relative overflow-hidden">
          {loading || !post ? (
            <div className="absolute inset-0 shimmer" />
          ) : post.media_url ? (
            <img
              src={post.media_url}
              alt={species[0] || "Bird of the day"}
              className="w-full h-full object-cover"
              data-testid="botd-image"
            />
          ) : (
            <div className="w-full h-full flex items-center justify-center text-[#8C7A6B]">
              <Bird size={64} strokeWidth={1.2} />
            </div>
          )}
        </div>
        <div className="md:col-span-2 flex-1 min-h-0 overflow-y-auto p-6 sm:p-8 lg:p-10 flex flex-col">
            <DialogHeader className="text-left">
              <div className="label-eyebrow mb-3 flex items-center gap-2">
                <Sparkles size={12} className="text-[#C17767]" strokeWidth={1.8} />
                Bird of the day
              </div>
              <DialogTitle
                data-testid="botd-title"
                className="font-serif-display text-3xl lg:text-4xl font-light tracking-tight text-[#1A1A1A]"
              >
                {loading
                  ? "Picking one for you…"
                  : species.length > 0
                  ? species.join(" · ")
                  : "An unnamed visitor"}
              </DialogTitle>
              <DialogDescription className="font-serif-display italic text-sm text-[#5C5C5C] mt-2">
                A random photograph from{" "}
                <a
                  href="https://instagram.com/inthegreenlane"
                  target="_blank"
                  rel="noreferrer"
                  className="text-[#2C5530] hover:text-[#1E3A21] underline decoration-[#C17767] underline-offset-4"
                >
                  @inthegreenlane
                </a>
                .
              </DialogDescription>
            </DialogHeader>

            {post?.caption && (
              <p className="mt-5 text-sm text-[#1A1A1A] leading-relaxed line-clamp-6 whitespace-pre-line">
                {post.caption}
              </p>
            )}

            <div className="mt-auto pt-6 flex flex-col sm:flex-row gap-3">
              <Button
                onClick={onSpinAgain}
                disabled={loading}
                variant="outline"
                data-testid="botd-spin-again"
                className="rounded-full border-[#2C5530] text-[#2C5530] hover:bg-[#2C5530] hover:text-white"
              >
                <Shuffle
                  size={14}
                  className={`mr-2 ${loading ? "animate-spin" : ""}`}
                />
                {loading ? "Picking…" : "Spin again"}
              </Button>
            </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const Home = () => {
  const [query, setQuery] = useState("");
  const [sort, setSort] = useState("recent");
  const [species, setSpecies] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [botdOpen, setBotdOpen] = useState(false);
  const [botdPost, setBotdPost] = useState(null);
  const [botdLoading, setBotdLoading] = useState(false);

  const loadBirdOfTheDay = async () => {
    setBotdLoading(true);
    try {
      const r = await axios.get(`${API}/random-post`);
      setBotdPost(r.data);
    } catch {
      toast.error("Couldn't fetch a bird right now");
    } finally {
      setBotdLoading(false);
    }
  };

  const openBirdOfTheDay = () => {
    setBotdOpen(true);
    loadBirdOfTheDay();
  };

  const fetchStats = async () => {
    try {
      const r = await axios.get(`${API}/stats`);
      setStats(r.data);
    } catch {
      // stats are non-critical; keep last known values
    }
  };

  const fetchSpecies = async () => {
    setLoading(true);
    try {
      const r = await axios.get(`${API}/species`, {
        params: { q: query || undefined, sort, limit: 500 },
      });
      setSpecies(r.data);
    } catch {
      toast.error("Failed to load species index");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;
    Promise.resolve().then(() => {
      if (!cancelled) fetchStats();
    });
    // Refresh stats every 90s so the "auto-sync · N min ago" indicator stays live
    const interval = setInterval(() => {
      if (!cancelled) fetchStats();
    }, 90000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  useEffect(() => {
    const t = setTimeout(fetchSpecies, 250); // debounce
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, sort]);

  const lastSync = useMemo(
    () => stats?.last_sync?.finished_at || stats?.last_sync?.started_at,
    [stats]
  );

  return (
    <div className="min-h-screen bg-[#FAF9F6]">
      <Header
        username={stats?.instagram_username || "inthegreenlane"}
        lastSync={lastSync}
      />
      <Hero stats={stats} onBirdOfTheDay={openBirdOfTheDay} />
      <SearchAndSort
        query={query}
        setQuery={setQuery}
        sort={sort}
        setSort={setSort}
        count={species.length}
      />

      <main
        data-testid="species-grid-section"
        className="max-w-7xl mx-auto px-6 lg:px-10 py-12"
      >
        {loading ? (
          <SkeletonGrid />
        ) : species.length === 0 ? (
          <EmptyState query={query} />
        ) : (
          <div
            data-testid="species-grid"
            className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6"
          >
            {species.map((s) => (
              <SpeciesCard key={s.id} species={s} onOpen={setSelected} />
            ))}
          </div>
        )}
      </main>

      <Footer username={stats?.instagram_username || "inthegreenlane"} />
      <SpeciesDialog species={selected} onClose={() => setSelected(null)} />
      <BirdOfTheDayDialog
        open={botdOpen}
        post={botdPost}
        loading={botdLoading}
        onSpinAgain={loadBirdOfTheDay}
        onClose={() => setBotdOpen(false)}
      />
      <Toaster
        position="bottom-right"
        toastOptions={TOAST_OPTIONS}
      />
    </div>
  );
};

function ScrollToTop() {
  const { pathname } = useLocation();
  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, [pathname]);
  return null;
}

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <ScrollToTop />
        <Routes>
          <Route path="/" element={<Home />} />
          <Route path="/rare-sightings" element={<RareSightingsPage />} />
          <Route path="/monthly-bird-map" element={<MonthlyBirdMapPage />} />
        </Routes>
      </BrowserRouter>
    </div>
  );
}

export default App;
